import Amplify, { Auth } from '@aws-amplify/auth';
import { FederatedIdName, amplifyConfig } from '../appsettings';
import React, { createContext, useEffect, useMemo, useReducer } from 'react';
import { createSession, generateJWTToken, userSessionActions } from '../../src/slices/userSessionSlice';

import { Hub } from '@aws-amplify/core';
import PropTypes from 'prop-types';
import { getUserProfile } from '../slices/settingSlice';
import { heartbeatActions } from '../slices/heartbeatSlice';
import log from 'loglevel';
import { messagingChannelActions } from '../../src/slices/messagingChannelSlice';
import { useDispatch } from 'react-redux';

let amplifyConfigured = false;

const initialAuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isLoading: true,
  user: null,
  error: null
};

export const getSession = () => {
  // Auth.currentSession() checks if token is expired and refreshes with Cognito if needed automatically
  return Auth.currentSession();
};

const handlers = {

  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, isLoading } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isLoading,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  LOGINERROR: (state, action) => {
    const { error } = action.payload;

    return {
      ...state,
      isAuthenticated: false,
      user: null,
      error
    };
  }
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialAuthState,
  method: 'Amplify',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve()
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const dispatch2 = useDispatch();

  const handleAuthFailure = ({ payload: { event, data } }) => {
    if (event === 'signIn_failure' || event === 'cognitoHostedUI_failure') {
      dispatch({
        type: 'LOGINERROR',
        payload: {
          error: data
        }
      });
    }
  };

  const configureAmplify = () => {
    if (!amplifyConfigured) {
      Amplify.configure(amplifyConfig);
      amplifyConfigured = true;
    }
  }
  const initializeUserSession = async () => {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const validUser = (await Auth.currentSession()).isValid();
      if (validUser) {
        const isAuthenticated = sessionStorage.getItem("IsAuthenticated");
        if (!isAuthenticated) {
          sessionStorage.setItem("IsAuthenticated", true)
        }
        dispatch2(userSessionActions.openDialog({
          open: false,
          msgType: null
        }));
        dispatch2(heartbeatActions.openDialog(false));
        const sessionProfile = sessionStorage.getItem('SettingProfile');
        if (sessionProfile === null) {
          dispatch2(createSession())
            .unwrap()
            .then(async () => {
              dispatch2(getUserProfile())
                .unwrap()
                .then((res) => {
                  sessionStorage.setItem('SettingAgencies', JSON.stringify(res.agencies));
                  sessionStorage.setItem('SettingAgenciesWithEndpoints', JSON.stringify(res.agencies_with_endpoints));
                  sessionStorage.setItem('SettingProfile', JSON.stringify(res.profile));
                  sessionStorage.setItem('SettingSuperAdminRole', JSON.stringify(res.super_admin_role));
                  sessionStorage.setItem("SuperAdminEmailDomains", JSON.stringify(res.superAdminEmailDomains));
                  sessionStorage.setItem("MaxAclDuration", JSON.stringify(res.maxAclDuration));
                  sessionStorage.setItem("MaxArchivedDuration", JSON.stringify(res.maxArchivedDuration));
                  sessionStorage.setItem("MaxArchivedExportDuration", JSON.stringify(res.maxArchivedExportDuration));
                  sessionStorage.setItem("CrossAgencies", JSON.stringify(res.cross_agencies));

                  const userId = res.profile.id;
                  subscribeMessage(userId)
                  dispatch({
                    type: 'INITIALIZE',
                    payload: {
                      isAuthenticated: true,
                      user: cognitoUser,
                      isLoading: false
                    }
                  });
                });
            })
            .catch((err) => {
              log.info(".............error occured in initialized amplify context........");
              if (err.message == "Request failed with status code 401" || err.message == "Request failed with status code 403") {
                dispatch2(userSessionActions.openDialog({
                  open: false,
                  msgType: null
                }));
              }
            })

        }
        else {
          const profile = JSON.parse(sessionStorage.getItem('SettingProfile'));
          const userId = profile.id;
          subscribeMessage(userId)
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: cognitoUser,
              isLoading: false
            }
          });
        }

      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            isLoading: true
          }
        });
      }
    } catch (err) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          isLoading: false
        }
      });
    }
  };

  useEffect(async () => {
    // listen before configure to get all event
    Hub.listen('auth', handleAuthFailure);
    configureAmplify();
    initializeUserSession(); 
  }, []);
  
  const subscribeMessage = (userId) => {
    dispatch2(generateJWTToken())
      .unwrap()
      .then((res) => {

        dispatch2(messagingChannelActions.startConnecting(res));
        dispatch2(messagingChannelActions.subscribeRecordingStatus({ userId }));
        dispatch2(messagingChannelActions.subscribeArchiveStatus({ userId }));
        dispatch2(messagingChannelActions.subscribeDownloadTrimStatus({ userId }));
        dispatch2(messagingChannelActions.subscribeExportCSVStatus({ userId }));
        dispatch2(messagingChannelActions.subscribeSessionStatus({ userId }));

        log.info("subscribed signalr with user id ", userId);
      });
  }
  const login = async () => {
    await Auth.federatedSignIn({ provider: FederatedIdName })
    const validUser = (await Auth.currentSession()).isValid();
    if (validUser) {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      dispatch({
        type: 'LOGIN',
        payload: {
          user: cognitoUser,
          isAuthenticated: true
        }
      });
    }
  };

  const logout = async () => {
    await Auth.signOut();
    dispatch({
      type: 'LOGOUT'
    });
  };

  const contextValue = useMemo(() => {
    return {
      ...state,
      method: 'Amplify',
      login,
      logout,
    };
  }, [state, login, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;